import React, {useState, useEffect, useRef} from 'react';
import {Button, Text, Checkbox} from '@/ui-kits';
import {SelectConsultationSpecialistRequestPayload} from '@/interface';
import {SpecialistCard} from '../SpecialistCard';
import {useDispatch, useSelector} from 'react-redux';
import {TermsConditionModal} from '@/components/common';
import {
  accountDeletionStatusSelector,
  patientDashboardConsultation,
  patientDashboardConsultationFlow,
  patientDashboardCurrentStep,
  patientDashboardManualCategorySelectionFlag,
  patientDashboardPaymentStatus,
  patientDashboardSpecialists,
  patientDashboardSpecialistSelected,
  selectSpecialistLoadingSelector,
  recentCasesSelector,
} from '@/services/redux/selectors';
import {
  selectSpecialistRequest,
  switchSpecialistFromPatientDashboardStep1,
  selectSpecialistAndProceedWithPaymentRequest,
  addFavouriteDoctorRequest,
  removeFavouriteDoctorRequest,
} from '@/services/redux/slices';
import {AccordianLayout} from '../AccordianLayout';
import {ConsulationFlowType, PatientAccountTicketRequestStatus, SELECT_SPECIALIST_CHECKPOINT} from '@/constants';
import {getErrorMessage} from '@/utils/getMessages';

// Main Select Specialist Component
interface SelectSpecialistProps {
  open: boolean;
  success: boolean;
}
export const SelectSpecialist: React.FC<SelectSpecialistProps> = props => {
  // ===========================
  // props
  // ===========================
  const {open, success} = props;
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();

  // ===========================
  // selectors
  // ===========================
  const consultation_id = useSelector(patientDashboardConsultation);
  const specialist = useSelector(patientDashboardSpecialists);
  const paymentStatus = useSelector(patientDashboardPaymentStatus);
  const persistedSelectedSpecialist = useSelector(patientDashboardSpecialistSelected);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const step = useSelector(patientDashboardCurrentStep);
  const consultationFlow = useSelector(patientDashboardConsultationFlow);
  const isManualFlag = useSelector(patientDashboardManualCategorySelectionFlag);
  const loading = useSelector(selectSpecialistLoadingSelector);
  const recentCases = useSelector(recentCasesSelector);
  // ===========================
  // states
  // ===========================
  const checkpoint = JSON.parse(localStorage.getItem(SELECT_SPECIALIST_CHECKPOINT) ?? '{}') as {
    selected_specialist_id?: number | null;
    terms_accepted?: boolean;
  };
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(checkpoint.selected_specialist_id ?? null);
  const [termsAccesspted, setTermsAccesspted] = useState<boolean>(checkpoint.terms_accepted ?? false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const selectedCardRef = useRef<HTMLDivElement | null>(null);

  // ===========================
  // functions
  // ===========================
  const handleSelectSpecialist = () => {
    if (!consultation_id || !selectedDoctorId || !termsAccesspted) return;
    const payload: SelectConsultationSpecialistRequestPayload = {
      id: consultation_id,
      specialist: selectedDoctorId,
    };

    if ([
      ConsulationFlowType.Referral, 
      ConsulationFlowType.FollowUp,
      ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP,
      ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL,
    ].includes(consultationFlow)) {
      dispatch(selectSpecialistAndProceedWithPaymentRequest(payload));
    } else {
      dispatch(selectSpecialistRequest(payload));
    }
  };

  function handleSelectCategory() {
    // change the doctor to null upon changing the category.
    setSelectedDoctorId(null);
  }

  // Get Last Consulted Date for a Doctor
  const getLastConsultedDate = (doctorName: string): string | null => {
    const recentCase = recentCases.find(
      (rc: any) => rc.doctor_name?.toLowerCase() === doctorName?.toLowerCase()
    );
    return recentCase?.close_at || null;
  };

  // Handle Favorite Toggle
  const handleFavoriteToggle = (doctorId: number) => {
    const doctor = specialist.find(s => s.id === doctorId);
    if (doctor?.is_favourite) {
      dispatch(removeFavouriteDoctorRequest({specialist_id: doctorId}));
    } else {
      dispatch(addFavouriteDoctorRequest({specialist_id: doctorId}));
    }
  };

  // ===========================
  // side effects
  // ===========================
  // Auto-select specialist when coming from pre-selected flow, followup or referral
  useEffect(() => {
    if (specialist.length === 0) return;

    // Check if there's a preselected specialist ID that matches a specialist in the list
    if (persistedSelectedSpecialist?.id) {
      const preselectedSpecialist = specialist.find(doc => doc.id === persistedSelectedSpecialist.id);
      if (preselectedSpecialist) {
        setSelectedDoctorId(persistedSelectedSpecialist.id);
        return;
      }
    }

    // For preselected flows, select the first specialist ONLY when manual category selection is OFF.
    // When manual category selection is ON and patient switches category, we should NOT auto-select
    // the first specialist from the new recommended list.
    if (
      [
        ConsulationFlowType.PreSelectedSpecialist,
        ConsulationFlowType.FollowUp,
        ConsulationFlowType.Referral,
        ConsulationFlowType.HospitalSpecialistPreSelected,
        ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP,
        ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL,
      ].includes(consultationFlow) &&
      !isManualFlag
    ) {
      setSelectedDoctorId(specialist[0].id);
    }
  }, [specialist, consultationFlow, persistedSelectedSpecialist, isManualFlag]);

  useEffect(() => {
    if (step === 2) {
      localStorage.setItem(
        SELECT_SPECIALIST_CHECKPOINT,
        JSON.stringify({
          selected_specialist_id: selectedDoctorId,
          terms_accepted: termsAccesspted,
        }),
      );
    }
  }, [selectedDoctorId, termsAccesspted, step]);

  // scroll to the selected card when the selected doctor id changes
  useEffect(() => {
    if (selectedCardRef.current && scrollContainerRef.current) {
      selectedCardRef.current.scrollIntoView({
        behavior: 'smooth', // or 'auto' if you want instant
        inline: 'center',   // centers horizontally
        block: 'nearest',
      });
    }
  }, [selectedDoctorId, specialist]);

  return (
    <AccordianLayout select onSelect={handleSelectCategory} open={open} success={success} icon="circle_2" key="Select Specialist" title="Select Specialist">
      {specialist.length > 0 ? (
        <div className="pb-6 sm:pb-8 m-2">
          <div className="overflow-x-auto overflow-y-visible" style={{WebkitOverflowScrolling: 'touch'}}>
            <div ref={scrollContainerRef} className="flex gap-3 sm:gap-4 pb-2 pt-8 min-w-max">
              {specialist.map(doctor => {
                const isSelected = doctor.id === selectedDoctorId;
                return <div ref={isSelected ? selectedCardRef : null} key={doctor.id} className="w-[300px] flex-shrink-0">
                   <SpecialistCard
                    disabled={paymentStatus === 'success'}
                    doctor={doctor}
                    compact={true}
                    isSelected={isSelected}
                    onSelect={id => {
                      setSelectedDoctorId(id);
                      dispatch(switchSpecialistFromPatientDashboardStep1());
                    }}
                    lastConsultedDate={getLastConsultedDate(doctor.name)}
                    showFavoriteIcon={true}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                </div>
              })}
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <div className="flex items-center">
              <Checkbox checked={termsAccesspted} disabled={!selectedDoctorId} onChange={setTermsAccesspted} />
              <Text className="ml-2">
                I agree to the{' '}
                <span
                  className="underline cursor-pointer hover:text-ui_blue/80 transition-colors"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsTermsModalOpen(true);
                  }}>
                  Terms and Condition
                </span>
              </Text>
            </div>
          </div>
          {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
            <div className="flex justify-center mt-4 sm:mt-6">
              <Button
                title="Next"
                isLoading={loading}
                disabled={paymentStatus === 'success' || !selectedDoctorId || !termsAccesspted}
                onClick={loading ? undefined : handleSelectSpecialist}
                className="max-w-[200px] w-full self-center rounded-lg"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 h-32 justify-center items-center">
          <Text variant="subtitle" className="text-ui_blue max-w-[700px] text-center !font-FigtreeSemiBold">
            {getErrorMessage('E-10076')}
          </Text>
        </div>
      )}
      <TermsConditionModal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
    </AccordianLayout>
  );
};
